import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Minus, Square, X, Copy } from "lucide-react";
import { MIN_H, MIN_W, useWindows, zoneRect } from "../WindowManager";
import { getLastPointer } from "../pointer";
import { getNode } from "../fs";
import "./Window.css";

/**
 * Keep in sync with the animation durations in Window.css. These are only a
 * safety net for when `animationend` never arrives (reduced motion, a
 * background tab, an interrupted animation) — the buffer keeps the timer from
 * cutting a running animation short, which reads as a stutter at the end.
 */
const ANIM_MS = { opening: 280, minimizing: 360, restoring: 340, closing: 190 };
const ANIM_BUFFER = 250;

const EDGE = 12; // px from a surface edge that arms a snap zone
const CORNER = 90; // px from a corner that arms a quadrant snap

function detectZone(px, py, W, H) {
  const nearLeft = px <= EDGE;
  const nearRight = px >= W - EDGE;
  const nearTop = py <= EDGE;
  const nearBottom = py >= H - EDGE;

  if (nearLeft && py <= CORNER) return "top-left";
  if (nearLeft && py >= H - CORNER) return "bottom-left";
  if (nearRight && py <= CORNER) return "top-right";
  if (nearRight && py >= H - CORNER) return "bottom-right";
  if (nearTop && px <= CORNER) return "top-left";
  if (nearTop && px >= W - CORNER) return "top-right";
  if (nearBottom && px <= CORNER) return "bottom-left";
  if (nearBottom && px >= W - CORNER) return "bottom-right";
  if (nearLeft) return "left";
  if (nearRight) return "right";
  if (nearTop) return "max";
  return null;
}

const HANDLES = ["n", "s", "e", "w", "ne", "nw", "se", "sw"];

export default function Window({ win, app, active, mobile, onSnapPreview }) {
  const { close, minimize, toggleMax, setRect, focus, snap, surface } =
    useWindows();
  const ref = useRef(null);
  const drag = useRef(null);
  const [showSnapLayouts, setShowSnapLayouts] = useState(false);
  const hoverTimer = useRef(null);
  const Body = app.component;

  // Windows titles a window after the document it has open.
  const docId = win.payload?.fileId || win.payload?.folderId;
  const docName = docId ? getNode(docId)?.name : null;
  const title = docName ? `${docName} — ${app.title}` : app.title;

  const geometry = win.maximized
    ? zoneRect("max", surface.w, surface.h)
    : { x: win.x, y: win.y, w: win.w, h: win.h };

  /* ------------------------------------------------ open / minimise genie */

  // Windows brought back by a refresh are already "there" — no launch genie.
  const [phase, setPhase] = useState(() =>
    win.restored ? (win.minimized ? "hidden" : "open") : "opening"
  );
  const wasMinimized = useRef(win.minimized);
  const phaseTimer = useRef(null);

  /** Window centre in viewport coordinates. */
  const centre = useCallback(() => {
    if (mobile) {
      return {
        x: surface.left + surface.w / 2,
        y: surface.top + surface.h / 2,
      };
    }
    return {
      x: surface.left + geometry.x + geometry.w / 2,
      y: surface.top + geometry.y + geometry.h / 2,
    };
  }, [geometry.h, geometry.w, geometry.x, geometry.y, mobile, surface]);

  /**
   * Points the animation at a target: the app's taskbar button when there is
   * one, otherwise the bottom centre of the screen.
   */
  const aimAt = useCallback(
    (target) => {
      const el = ref.current;
      if (!el) return;
      const from = centre();
      el.style.setProperty("--gx", `${target.x - from.x}px`);
      el.style.setProperty("--gy", `${target.y - from.y}px`);
    },
    [centre]
  );

  const taskbarPoint = useCallback(() => {
    const btn = document.querySelector(`[data-taskbar-app="${win.appId}"]`);
    if (btn) {
      const r = btn.getBoundingClientRect();
      return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
    }
    return { x: window.innerWidth / 2, y: window.innerHeight };
  }, [win.appId]);

  /* Opening: grow out of whatever the visitor just clicked. */
  useLayoutEffect(() => {
    aimAt(getLastPointer() || taskbarPoint());
    // Only ever runs for the initial mount.
    // eslint-disable-next-line
  }, []);

  /* Minimising and restoring both aim at the taskbar button. */
  useLayoutEffect(() => {
    if (win.minimized === wasMinimized.current) return;
    wasMinimized.current = win.minimized;
    aimAt(taskbarPoint());
    setPhase(win.minimized ? "minimizing" : "restoring");
  }, [aimAt, taskbarPoint, win.minimized]);

  /* Animations can be skipped (reduced motion) or interrupted — never leave a
     window stuck mid-phase. */
  useEffect(() => {
    clearTimeout(phaseTimer.current);
    const after = (fn) =>
      setTimeout(fn, ANIM_MS[phase] + ANIM_BUFFER);

    if (phase === "opening" || phase === "restoring") {
      phaseTimer.current = after(() => setPhase("open"));
    } else if (phase === "minimizing") {
      phaseTimer.current = after(() => setPhase("hidden"));
    } else if (phase === "closing") {
      phaseTimer.current = after(() => close(win.id));
    }
    return () => clearTimeout(phaseTimer.current);
  }, [phase, close, win.id]);

  const onAnimationEnd = (e) => {
    if (e.target !== e.currentTarget) return; // ignore descendants
    if (phase === "minimizing") setPhase("hidden");
    else if (phase === "closing") close(win.id);
    else if (phase === "opening" || phase === "restoring") setPhase("open");
  };

  /* ---------------------------------------------------------------- drag */

  const onTitlePointerDown = useCallback(
    (e) => {
      if (mobile || e.button !== 0) return;
      if (e.target.closest("[data-no-drag]")) return;
      focus(win.id);

      const rect = ref.current.getBoundingClientRect();
      const restore = win.restoreRect || { w: win.w, h: win.h };
      drag.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        offsetX: e.clientX - rect.left,
        offsetY: e.clientY - rect.top,
        w: rect.width,
        h: rect.height,
        wasSnapped: Boolean(win.maximized || win.snap),
        restoreW: restore.w,
        restoreH: restore.h,
        moved: false,
        zone: null,
      };
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [focus, mobile, win]
  );

  const onTitlePointerMove = useCallback(
    (e) => {
      const d = drag.current;
      if (!d || d.pointerId !== e.pointerId) return;

      if (!d.moved) {
        if (Math.abs(e.clientX - d.startX) + Math.abs(e.clientY - d.startY) < 5)
          return;
        d.moved = true;
        ref.current.classList.add("win-window--dragging");
        if (d.wasSnapped) {
          // Un-snap under the cursor, Windows-style.
          const ratio = d.offsetX / d.w;
          d.w = d.restoreW;
          d.h = d.restoreH;
          d.offsetX = ratio * d.restoreW;
          d.offsetY = Math.min(d.offsetY, 24);
          ref.current.style.width = `${d.w}px`;
          ref.current.style.height = `${d.h}px`;
        }
      }

      const px = e.clientX - surface.left;
      const py = e.clientY - surface.top;
      const x = Math.max(
        -d.w + 120,
        Math.min(px - d.offsetX, surface.w - 120)
      );
      const y = Math.max(0, Math.min(py - d.offsetY, surface.h - 40));
      ref.current.style.left = `${x}px`;
      ref.current.style.top = `${y}px`;

      const zone = detectZone(px, py, surface.w, surface.h);
      if (zone !== d.zone) {
        d.zone = zone;
        onSnapPreview(zone);
      }
    },
    [onSnapPreview, surface]
  );

  const endDrag = useCallback(
    (e) => {
      const d = drag.current;
      if (!d || d.pointerId !== e.pointerId) return;
      drag.current = null;
      onSnapPreview(null);
      ref.current.classList.remove("win-window--dragging");
      if (!d.moved) return;

      if (d.zone) {
        snap(win.id, d.zone);
        return;
      }
      setRect(win.id, {
        x: parseFloat(ref.current.style.left),
        y: parseFloat(ref.current.style.top),
        w: d.w,
        h: d.h,
      });
    },
    [onSnapPreview, setRect, snap, win.id]
  );

  /* -------------------------------------------------------------- resize */

  const onResizePointerDown = useCallback(
    (e, dir) => {
      if (mobile || e.button !== 0) return;
      e.stopPropagation();
      focus(win.id);
      const rect = ref.current.getBoundingClientRect();
      drag.current = {
        pointerId: e.pointerId,
        dir,
        startX: e.clientX,
        startY: e.clientY,
        x: rect.left - surface.left,
        y: rect.top - surface.top,
        w: rect.width,
        h: rect.height,
      };
      ref.current.classList.add("win-window--dragging");
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [focus, mobile, surface.left, surface.top, win.id]
  );

  const onResizePointerMove = useCallback(
    (e) => {
      const d = drag.current;
      if (!d || d.pointerId !== e.pointerId || !d.dir) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      let { x, y, w, h } = d;

      if (d.dir.includes("e")) w = Math.max(MIN_W, d.w + dx);
      if (d.dir.includes("s")) h = Math.max(MIN_H, d.h + dy);
      if (d.dir.includes("w")) {
        w = Math.max(MIN_W, d.w - dx);
        x = d.x + (d.w - w);
      }
      if (d.dir.includes("n")) {
        h = Math.max(MIN_H, d.h - dy);
        y = d.y + (d.h - h);
      }

      const el = ref.current;
      el.style.left = `${x}px`;
      el.style.top = `${y}px`;
      el.style.width = `${w}px`;
      el.style.height = `${h}px`;
    },
    []
  );

  const endResize = useCallback(
    (e) => {
      const d = drag.current;
      if (!d || d.pointerId !== e.pointerId || !d.dir) return;
      drag.current = null;
      const el = ref.current;
      el.classList.remove("win-window--dragging");
      setRect(win.id, {
        x: parseFloat(el.style.left),
        y: parseFloat(el.style.top),
        w: parseFloat(el.style.width),
        h: parseFloat(el.style.height),
      });
    },
    [setRect, win.id]
  );

  /* ------------------------------------------------------ snap flyout ux */

  useEffect(() => () => clearTimeout(hoverTimer.current), []);

  const armSnapLayouts = () => {
    if (mobile) return;
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setShowSnapLayouts(true), 420);
  };

  const disarmSnapLayouts = () => {
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setShowSnapLayouts(false), 180);
  };

  // Every app tints its own window through --app.
  const style = mobile
    ? { zIndex: win.z, "--app": app.color }
    : {
        left: geometry.x,
        top: geometry.y,
        width: geometry.w,
        height: geometry.h,
        zIndex: win.z,
        "--app": app.color,
      };

  return (
    <section
      ref={ref}
      className={[
        "win-window",
        `win-window--${phase}`,
        active ? "win-window--active" : "",
        win.maximized ? "win-window--maximized" : "",
        mobile ? "win-window--sheet" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={style}
      role="dialog"
      aria-label={title}
      aria-hidden={win.minimized}
      onAnimationEnd={onAnimationEnd}
      onPointerDown={() => focus(win.id)}
    >
      <header
        className="win-titlebar"
        onPointerDown={onTitlePointerDown}
        onPointerMove={onTitlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onDoubleClick={() => !mobile && toggleMax(win.id)}
      >
        <span className="win-titlebar__icon" style={{ color: app.color }}>
          <app.icon size={15} strokeWidth={2} />
        </span>
        <span className="win-titlebar__text">{title}</span>

        <div className="win-caption" data-no-drag>
          <button
            className="win-caption__btn"
            onClick={() => minimize(win.id)}
            aria-label="Minimize"
            title="Minimize"
          >
            <Minus size={15} strokeWidth={1.6} />
          </button>
          {!mobile && (
            <button
              className="win-caption__btn"
              onClick={() => toggleMax(win.id)}
              onMouseEnter={armSnapLayouts}
              onMouseLeave={disarmSnapLayouts}
              aria-label={win.maximized ? "Restore" : "Maximize"}
              title={win.maximized ? "Restore" : "Maximize"}
            >
              {win.maximized ? (
                <Copy size={13} strokeWidth={1.6} />
              ) : (
                <Square size={12} strokeWidth={1.8} />
              )}
            </button>
          )}
          <button
            className="win-caption__btn win-caption__btn--close"
            onClick={() => setPhase("closing")}
            aria-label="Close"
            title="Close"
          >
            <X size={16} strokeWidth={1.6} />
          </button>

          {showSnapLayouts && (
            <div
              className="snap-flyout"
              onMouseEnter={armSnapLayouts}
              onMouseLeave={disarmSnapLayouts}
            >
              <div className="snap-flyout__grid">
                <button
                  className="snap-layout snap-layout--halves"
                  onClick={() => snap(win.id, "left")}
                  aria-label="Snap left half"
                >
                  <i className="snap-cell" />
                  <i className="snap-cell snap-cell--dim" />
                </button>
                <button
                  className="snap-layout snap-layout--halves"
                  onClick={() => snap(win.id, "right")}
                  aria-label="Snap right half"
                >
                  <i className="snap-cell snap-cell--dim" />
                  <i className="snap-cell" />
                </button>
                <button
                  className="snap-layout snap-layout--quad"
                  onClick={() => snap(win.id, "top-left")}
                  aria-label="Snap top left"
                >
                  <i className="snap-cell" />
                  <i className="snap-cell snap-cell--dim" />
                  <i className="snap-cell snap-cell--dim" />
                  <i className="snap-cell snap-cell--dim" />
                </button>
                <button
                  className="snap-layout snap-layout--quad"
                  onClick={() => snap(win.id, "bottom-right")}
                  aria-label="Snap bottom right"
                >
                  <i className="snap-cell snap-cell--dim" />
                  <i className="snap-cell snap-cell--dim" />
                  <i className="snap-cell snap-cell--dim" />
                  <i className="snap-cell" />
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      <div className="win-window__body win-scroll">
        {/* payloadKey remounts the app when a new document is opened into it */}
        <Body payload={win.payload} winId={win.id} key={win.payloadKey} />
      </div>

      {!mobile &&
        HANDLES.map((dir) => (
          <span
            key={dir}
            className={`win-resize win-resize--${dir}`}
            onPointerDown={(e) => onResizePointerDown(e, dir)}
            onPointerMove={onResizePointerMove}
            onPointerUp={endResize}
            onPointerCancel={endResize}
          />
        ))}
    </section>
  );
}
